import React, { useRef } from 'react';
import { Form } from '@dashboard/components/Formik';
import {
  DEFAULT_GUESTS_DESCRIPTION,
  DEFAULT_GUESTS_HEADING,
  DEFAULT_GUESTS_SAVE_BUTTON,
} from '@repo/lib';
import { GUEST_DESCRIPTION_MAX_LENGTH, GUEST_SAVE_BUTTON_MAX_LENGTH } from '@repo/schemas';
import usePage from '@dashboard/hooks/usePage';

const EMAIL_DOMAIN_OPTIONS = [
  { value: 'any', label: 'Any domain' },
  { value: 'work', label: 'Work domains only' },
  { value: 'same', label: 'Work domains only, same as the booker' },
];
const DOMAIN_STATUS = { any: 'Any domain', work: 'Work domains only', same: 'Same as the booker' };

// One stored setting, shown as which domains are allowed plus whether to flag the rest.
const FLAG_LABELS = {
  any: 'Flag public domains for review',
  work: "Flag domains that don't match the booker's",
};
const DOMAIN_RULE = { any: 'any', flag: 'any', work: 'work', workFlag: 'work', same: 'same' };
const FLAGGED_RULE = { any: 'flag', work: 'workFlag' };

export function emailDomainsFrom({ domainRule, flagDomains }) {
  return (flagDomains && FLAGGED_RULE[domainRule]) || domainRule;
}

export function getGroupRegistrationFormValues(settings) {
  return {
    enabled: Boolean(settings?.enabled),
    when: settings?.when || 'checkout',
    requireAllBeforePayment: settings?.requireAllBeforePayment ?? settings?.when !== 'afterBooking',
    domainRule: DOMAIN_RULE[settings?.emailDomains] ?? 'any',
    flagDomains: ['flag', 'workFlag'].includes(settings?.emailDomains),
    sendReminders: settings?.sendReminders ?? true,
    heading: settings?.heading ?? DEFAULT_GUESTS_HEADING,
    description: settings?.description ?? DEFAULT_GUESTS_DESCRIPTION,
    saveButton: settings?.saveButton || DEFAULT_GUESTS_SAVE_BUTTON,
  };
}

export const settingStatus = {
  when: values =>
    values.when === 'afterBooking'
      ? 'After booking'
      : `At checkout${values.requireAllBeforePayment ? ', required' : ''}`,
  domains: values =>
    `${DOMAIN_STATUS[values.domainRule]}${
      values.flagDomains && FLAG_LABELS[values.domainRule] ? ', flagged' : ''
    }`,
  reminders: values => (values.sendReminders ? 'On' : 'Off'),
  heading: values => values.heading.trim() || 'No heading',
};

// The switch and the settings rows are separate forms, so each saves only its own values over the
// event's latest settings: neither can write back a value the other just changed.
function useSaveSettings(ownKeys) {
  const { page, updatePage } = usePage();
  const latest = useRef(page);
  latest.current = page;

  return async ({ domainRule, flagDomains, ...values }) => {
    const all = { ...values, emailDomains: emailDomainsFrom({ domainRule, flagDomains }) };
    const current = latest.current.eventDetails;
    const attendeeDetails = current?.attendeeDetails
      ? {
          ...current.attendeeDetails,
          ...Object.fromEntries(ownKeys.map(key => [key, all[key]])),
        }
      : all;
    const { data, error } = await updatePage.mutateAsync({
      eventDetails: { ...current, attendeeDetails },
    });
    return { data, error };
  };
}

export const CollectAttendeesSwitch = () => {
  const { page } = usePage();
  const save = useSaveSettings(['enabled']);

  return (
    <Form
      autosave
      showState={false}
      initialValues={getGroupRegistrationFormValues(page.eventDetails?.attendeeDetails)}
      fields={[{ name: 'enabled', label: 'Collect attendee details', type: 'toggle' }]}
      onSubmit={save}
    />
  );
};

const GroupRegistrationSettingsForm = () => {
  const { page } = usePage();
  const save = useSaveSettings([
    'when',
    'requireAllBeforePayment',
    'emailDomains',
    'sendReminders',
    'heading',
    'description',
    'saveButton',
  ]);

  return (
    <Form
      autosave
      showState={false}
      initialValues={getGroupRegistrationFormValues(page.eventDetails?.attendeeDetails)}
      fields={({ values, setFieldValue }) => [
        {
          name: 'settings',
          type: 'accordionGroup',
          startClosed: true,
          items: [
            {
              name: 'when',
              label: 'When to collect details',
              status: settingStatus.when,
              fields: [
                {
                  name: 'when',
                  type: 'radioCard',
                  columns: 2,
                  options: [
                    {
                      value: 'checkout',
                      label: 'At checkout',
                      labelDescription: 'Collect attendee details while booking.',
                    },
                    {
                      value: 'afterBooking',
                      label: 'After booking',
                      labelDescription: 'Collect attendee details once the booking is complete.',
                    },
                  ],
                  onChange: value => {
                    if (value === 'afterBooking') setFieldValue('requireAllBeforePayment', false);
                  },
                },
                {
                  name: 'requireAllBeforePayment',
                  label: 'Require details for every attendee before booking',
                  type: 'checkbox',
                  disabled: values.when === 'afterBooking',
                },
              ],
            },
            {
              name: 'domains',
              label: 'Email domains',
              status: settingStatus.domains,
              fields: [
                {
                  name: 'domainRule',
                  type: 'select',
                  options: EMAIL_DOMAIN_OPTIONS,
                  // Each rule's checkbox flags something different, so a tick never carries over.
                  onChange: () => setFieldValue('flagDomains', false),
                  helpText:
                    "Choose which email addresses attendees can use. A work domain is a company's own, like name@acme.com. Public domains are free email, like gmail.com and outlook.com.",
                },
                ...(FLAG_LABELS[values.domainRule]
                  ? [
                      {
                        name: 'flagDomains',
                        label: FLAG_LABELS[values.domainRule],
                        type: 'checkbox',
                      },
                    ]
                  : []),
              ],
            },
            {
              name: 'reminders',
              label: 'Reminders',
              status: settingStatus.reminders,
              fields: [
                {
                  name: 'sendReminders',
                  label: 'Remind the customer to add attendee details',
                  type: 'checkbox',
                  helpText:
                    'We email the customer reminders until attendees are added: 24 hours after booking, twice more at 3-day intervals, and 3 days before the event. They add them in the customer portal.',
                },
              ],
            },
            {
              name: 'heading',
              label: 'Checkout text',
              status: settingStatus.heading,
              fields: [
                {
                  name: 'heading',
                  label: 'Heading',
                  type: 'text',
                  placeholder: DEFAULT_GUESTS_HEADING,
                  clearable: true,
                  helpText: 'Shown above the attendees at checkout and on the confirmation page.',
                },
                {
                  name: 'description',
                  label: 'Description',
                  type: 'text',
                  placeholder: DEFAULT_GUESTS_DESCRIPTION,
                  maxLength: GUEST_DESCRIPTION_MAX_LENGTH,
                  showCount: true,
                  clearable: true,
                  helpText: 'Shown under the heading.',
                },
                {
                  name: 'saveButton',
                  label: 'Save button',
                  type: 'text',
                  placeholder: DEFAULT_GUESTS_SAVE_BUTTON,
                  maxLength: GUEST_SAVE_BUTTON_MAX_LENGTH,
                  showCount: true,
                  helpText: 'Shown on the button that saves each attendee.',
                },
              ],
            },
          ],
        },
      ]}
      onSubmit={save}
    />
  );
};

export default GroupRegistrationSettingsForm;
